import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'

import { IOS_MODAL_DISMISS_FALLBACK_MS } from '../helpers/nativeModalDismiss.helper'

interface UseCloseModalThenOptions {
  /** Asks the Modal to close (sets its `visible` to false). */
  onClose: () => void
  /** Optional outer Modal to close as well before the action runs (e.g. the global menu). */
  closeHostThen?: (then: () => void) => void
}

export interface UseCloseModalThenResult {
  /** Close the Modal, then run `action` once it is really gone. */
  closeThen: (action: () => void) => void
  /** Wire to the Modal's `onDismiss` (fires on iOS when the dismiss animation completes). */
  onModalDismiss: () => void
}

/**
 * Defers an action that opens another native Modal until the current one has closed.
 *
 * iOS refuses to present a Modal while another is still animating away, and RN then believes
 * it is on screen: the new Modal never appears and the app can stop taking touches
 * (specs/profile-page, Story 8). Android and web have no such limit, so there the action runs
 * straight away, as before.
 */
export function useCloseModalThen({
  onClose,
  closeHostThen,
}: UseCloseModalThenOptions): UseCloseModalThenResult {
  const pendingActionRef = useRef<(() => void) | null>(null)
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeHostThenRef = useRef(closeHostThen)

  useEffect(() => {
    closeHostThenRef.current = closeHostThen
  }, [closeHostThen])

  const runPendingAction = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }
    const action = pendingActionRef.current
    pendingActionRef.current = null
    if (!action) return
    const closeHost = closeHostThenRef.current
    if (closeHost) closeHost(action)
    else action()
  }, [])

  const closeThen = useCallback(
    (action: () => void) => {
      pendingActionRef.current = action
      onClose()
      if (Platform.OS === 'ios') {
        // Normally onModalDismiss runs it; the timer only covers a missing dismiss event
        // (the Modal unmounted while closing). Deliberately not cleared on unmount.
        if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = setTimeout(runPendingAction, IOS_MODAL_DISMISS_FALLBACK_MS)
      } else {
        runPendingAction()
      }
    },
    [onClose, runPendingAction],
  )

  return { closeThen, onModalDismiss: runPendingAction }
}
